<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute } from 'vue-router';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { FilterMatchMode } from '@primevue/core/api';
import type { DataTableFilterEvent } from 'primevue/datatable';
import KpMandantKantonSelect from '@/components/KpMandantKantonSelect.vue';
import KpJahrSelect from '@/components/KpJahrSelect.vue';
import KpTextField from '@/components/KpTextField.vue';
import { GesamtenergieverbrauchProGebaeudeCollection } from '@/models/GesamtenergieverbrauchProGebaeude';

const route = useRoute();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'id' },
    { direction: 'desc', property: 'jahr' },
    { direction: 'asc', property: 'kanton_id' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const { collection, serverTotal } = useCollection(new GesamtenergieverbrauchProGebaeudeCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

type Filter = {
    property: string;
    value: any;
    id: string;
    operator?: string;
};
const filters = ref({
    kanton_id: { value: null, matchMode: FilterMatchMode.EQUALS },
    jahr: { value: null, matchMode: FilterMatchMode.EQUALS },
    egid: { value: null, matchMode: FilterMatchMode.CONTAINS },
    nutzungsart: { value: null, matchMode: FilterMatchMode.CONTAINS },
    angrenzende_gebaeude: { value: null, matchMode: FilterMatchMode.CONTAINS },
    bfs_gemeindenummer: { value: null, matchMode: FilterMatchMode.CONTAINS },
    bauperiode_id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energiebezugsflaeche: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_heizung_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_heizung_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_heizung_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_heizung_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_warmwasser_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_warmwasser_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_warmwasser_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_warmwasser_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    nutzungsgrad_heizung_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    nutzungsgrad_heizung_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    nutzungsgrad_warmwasser_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    nutzungsgrad_warmwasser_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    e_gebaeudekoordinate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    n_gebaeudekoordinate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ist_grossverbraucher: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ist_waermeverbund: { value: null, matchMode: FilterMatchMode.CONTAINS },
    grossverbraucher_ids: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name_grossverbraucher: { value: null, matchMode: FilterMatchMode.CONTAINS },
    endenergie_beruecksichtigen: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermebezug_von_grossverbraucher_oder_waermeverbund: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermebezug_id: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const filter: Ref<Filter[]> = ref([]);
function onFilterChange(filterEvent: DataTableFilterEvent) {
    const prop = 'value';
    filter.value = [];
    for (const key in filterEvent.filters) {
        // @ts-expect-error it's save, do it!
        const value = filterEvent.filters[key].hasOwnProperty(prop) ? filterEvent.filters[key][prop] : null;
        filter.value.push({
            property: key,
            id: key,
            operator: '=',
            value,
        });
    }
    collection.setQueryParams({ filter: filter.value });
    collection.query();
}

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :showRecordEditBtn="false"
            :showRecordDeleteBtn="false"
            :showReloadBtn="false"
            :showCsvDownloadBtn="false"
            :showAddBtn="false"
            :showCopyBtn="false"
            :collection="collection"
            v-model:table-sort="tableSort"
            v-model:filters="filters"
            :title="$t('gesamtenergieverbrauch_pro_gebaeude')"
            filterDisplay="row"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @filter="onFilterChange"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column
                field="kanton"
                sortable
                :header="$t('kanton')"
                style="width: 5rem"
                :showFilterMenu="false"
                sortField="kanton_id"
                filterField="kanton_id"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpMandantKantonSelect
                        v-model="filterModel.value"
                        fluid
                        @change="filterCallback()"
                    ></KpMandantKantonSelect>
                </template>
            </Column>
            <Column field="jahr" sortable :header="$t('jahr')" style="width: 5rem" :showFilterMenu="false">
                <template #filter="{ filterModel, filterCallback }">
                    <KpJahrSelect
                        fluid
                        :start-year="2015"
                        v-model="filterModel.value"
                        @change="filterCallback()"
                    ></KpJahrSelect> </template
            ></Column>
            <Column field="egid" sortable :header="$t('egid')" style="width: 5rem"></Column>
            <Column field="nutzungsart" sortable :header="$t('nutzungsart')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="angrenzende_gebaeude" sortable :header="$t('angrenzende_gebaeude')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="bfs_gemeindenummer" sortable :header="$t('bfs_gemeindenummer')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="bauperiode_id" sortable :header="$t('bauperiode_id')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="energiebezugsflaeche" sortable :header="$t('energiebezugsflaeche')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_heizung_1" sortable :header="$t('waermeerzeuger_heizung_1')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_heizung_2" sortable :header="$t('waermeerzeuger_heizung_2')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="energie_waermequelle_heizung_1" sortable :header="$t('energie_waermequelle_heizung_1')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="energie_waermequelle_heizung_2" sortable :header="$t('energie_waermequelle_heizung_2')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_warmwasser_1" sortable :header="$t('waermeerzeuger_warmwasser_1')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_warmwasser_2" sortable :header="$t('waermeerzeuger_warmwasser_2')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="energie_waermequelle_warmwasser_1"
                sortable
                :header="$t('energie_waermequelle_warmwasser_1')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="energie_waermequelle_warmwasser_2"
                sortable
                :header="$t('energie_waermequelle_warmwasser_2')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="nutzungsgrad_heizung_1" sortable :header="$t('nutzungsgrad_heizung_1')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="nutzungsgrad_heizung_2" sortable :header="$t('nutzungsgrad_heizung_2')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="nutzungsgrad_warmwasser_1" sortable :header="$t('nutzungsgrad_warmwasser_1')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="nutzungsgrad_warmwasser_2" sortable :header="$t('nutzungsgrad_warmwasser_2')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="e_gebaeudekoordinate" sortable :header="$t('e_gebaeudekoordinate')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="n_gebaeudekoordinate" sortable :header="$t('n_gebaeudekoordinate')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="ist_grossverbraucher" sortable :header="$t('ist_grossverbraucher')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="ist_waermeverbund" sortable :header="$t('ist_waermeverbund')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="grossverbraucher_ids" sortable :header="$t('grossverbraucher_ids')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="name_grossverbraucher" sortable :header="$t('name_grossverbraucher')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="endenergie_beruecksichtigen" sortable :header="$t('endenergie_beruecksichtigen')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="waermebezug_von_grossverbraucher_oder_waermeverbund"
                sortable
                :header="$t('waermebezug_von_grossverbraucher_oder_waermeverbund')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermebezug_id" sortable :header="$t('waermebezug_id')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
        </KpBaseCollectionGrid>
    </div>
</template>
